'use client';

import LinkButton from '@carvana/ds-link-button';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { DispatcherEvent } from '@carvana/merch-native';
import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/navigation';
import { useContext } from 'react';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useIsNative } from '~/context/common/DeviceContext';
import {
  useIsNativeCapabilityActive,
  useSendMessageToNative
} from '~/context/common/NativeContext';
import {
  useClosedAccountModal,
  useOpenAccountModal
} from '~/hooks/common/accountModal/useAccountModal';
import { logError } from '~/utilities/loggers';
import { LoginPromptText } from './Hero.styles';

export default function LoginPrompt() {
  const closedAccountModal = useClosedAccountModal();
  const hasLoginButtonCapability =
    useIsNativeCapabilityActive('FlpLoginButton');
  const isNative = useIsNative();
  const openAccountModal = useOpenAccountModal();
  const router = useRouter();
  const sendMessageToNative = useSendMessageToNative<DispatcherEvent>();
  const { trackEvent } = useContext(AnalyticsContext);

  if (isNative && !hasLoginButtonCapability) {
    return null;
  }

  const handleLoginSuccess = () => {
    trackEvent(ANALYTICS_EVENTS.MARKETING.LOGIN_SUCCEEDED);
    closedAccountModal('submit');
    router.refresh();
  };

  const handleSignInClick = () => {
    datadogRum.addAction(DATADOG_EVENTS.MARKETING.CLICKS_LOGIN_CTA);
    trackEvent(ANALYTICS_EVENTS.MARKETING.CLICKS_LOGIN_CTA);

    if (isNative) {
      sendMessageToNative(
        DispatcherEvent.LogIn,
        {},
        {
          callback: () => {
            // Native calls this after the user signs in, mirroring the web modal's onSuccess.
            window.carvana = window.carvana ?? {};
            window.carvana.onLoginSuccess = handleLoginSuccess;
          },
          onError: error =>
            logError(error, { location: 'LoginPrompt native log_in' })
        }
      );

      return;
    }

    openAccountModal({ onSuccess: handleLoginSuccess });
  };

  return (
    <LoginPromptText data-testid="flp-login-prompt">
      Have an account?{' '}
      <LinkButton
        data-analytics-id="Financing Hero Sign In Link"
        data-testid="flp-login"
        underline={false}
        onClick={handleSignInClick}
      >
        Sign In
      </LinkButton>
    </LoginPromptText>
  );
}
